/**
 * PGSS-5803 (PSG, 21 Oct, KPI): who picked an outgoing shipment, one record per pallet, and pallets per picker.
 * Pure helpers and the API paths; the server decides everything.
 */
import { REQUEST_TIMEOUT_MS } from 'app/main/apps/scanToLoad/scanToLoadRules';

export const PICKERS_API = '/api/shipment-pickers';
export const PICKER_KPI_API = '/api/shipment-kpi/pickers';

export function pickingApi(shipmentId) {
  return `/api/customer-shipments/${shipmentId}/picking`;
}

export function palletsApi(shipmentId) {
  return `/api/customer-shipments/${shipmentId}/pallets`;
}

/** One pallet, to remove it (added by mistake). */
export function palletApi(shipmentId, palletId) {
  return `${palletsApi(shipmentId)}/${palletId}`;
}

/** What the pick ticket's QR code opens: the shipment's Scan to load, where picking starts. '' without a shipment. */
export function pickTicketScanUrl(origin, shipmentId) {
  if (!shipmentId) return '';
  return `${String(origin || '').replace(/\/+$/, '')}/shipments/load/${shipmentId}`;
}

/** The pickers to choose from (turned on), or [] when they can't be read. */
export async function fetchPickers(get, all = false) {
  try {
    const { data } = await get(PICKERS_API, { params: all ? { all: true } : {}, timeout: REQUEST_TIMEOUT_MS });
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/** Uploads the pallet photo and returns its file id. */
export async function uploadPalletPhoto(post, file) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('addTo', 'shipments');
  const { data } = await post('/api/files', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  const id = data?.file?.id;
  if (!id) throw new Error('The photo was not saved. Try again.');
  return id;
}

/** "Picking started by Ravi, Oct 20, 2:15 PM", or '' before it started. */
export function pickingStartedText(steps) {
  if (!steps?.pickingStartedAt) return '';
  const when = new Date(steps.pickingStartedAt).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
  });
  return `Picking started${steps.pickerName ? ` by ${steps.pickerName}` : ''}, ${when}`;
}

/** Why a pallet can't be added yet, or ''. */
export function addPalletProblem({ pickerId, photo }) {
  if (!pickerId) return 'Choose the picker.';
  if (!photo) return 'Add the pallet photo.';
  return '';
}

/**
 * The KPI answer: rows by period, and each picker's totals for the range, as the server counts them (review of #2781:
 * a shipment whose pallets fall on two days is one shipment, which adding up the rows would count twice).
 */
export function kpiReport(data) {
  return {
    rows: Array.isArray(data?.rows) ? data.rows : [],
    totals: Array.isArray(data?.totals) ? data.totals : [],
  };
}

/** PGSS-5870: when a pallet was added, with its date, in PSG's time zone: "Nov 2, 2026, 3:15 PM". */
export function palletTimeText(createdAt) {
  const at = createdAt ? new Date(createdAt) : null;
  if (!at || Number.isNaN(at.getTime())) return '';
  return at.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
  });
}
